import type { MiniCanvas } from '@openapi'
import type { SfAccountMini, SfEvent, SfUser } from '@lib/types/sfdc'
import { SfEventColors } from '@lib/types/sfdc'
import { isAfter, isBefore, add, sub, set, getTime, startOfDay } from 'date-fns'
import type { ComputedRef } from 'vue'
import { titleCase } from '@lib/util/casing'

export function extractFieldNamesFromCondition(condition: string): string[] {
  if (!condition) return []

  const stringLiteralPattern = /(["'])(?:(?=(\\?))\2.)*?\1/g
  const conditionWithoutStrings = condition.replace(stringLiteralPattern, '')

  const fieldPattern = /\b([A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)?(?:__[cr])?)\b/g
  const matches = conditionWithoutStrings.match(fieldPattern) || []

  const soqlKeywords = new Set([
    'AND',
    'OR',
    'NOT',
    'IN',
    'LIKE',
    'NULL',
    'TRUE',
    'FALSE',
    'true',
    'false',
    'null',
    'and',
    'or',
    'not',
    'in',
    'like',
  ])

  return [...new Set(matches.filter((field) => !soqlKeywords.has(field)))]
}

export function evaluateIsExecutedCondition(condition: string | undefined, event: any): boolean {
  if (!condition) return false // No condition means we can't determine, default to not executed

  try {
    let jsCondition = condition
      .replace(/<>/g, '!==') // Not equal (SOQL style)
      .replace(/!=/g, '!==') // Not equal
      .replace(/([^!<>=])\s*=\s*([^=])/g, '$1 === $2') // Equals (only standalone =, not part of !=, >=, <=, ==)
      .replace(/\bNULL\b/gi, 'null')
      .replace(/\bTRUE\b/gi, 'true')
      .replace(/\bFALSE\b/gi, 'false')
      .replace(/\bAND\b/gi, '&&')
      .replace(/\bOR\b/gi, '||')
      .replace(/\bNOT\b/gi, '!')

    // Extract field names and replace them with event property access
    const fieldNames = extractFieldNamesFromCondition(condition)
    fieldNames.forEach((fieldName) => {
      const fieldValue = event[fieldName]
      const jsValue = fieldValue === null || fieldValue === undefined ? 'null' : JSON.stringify(fieldValue)
      const fieldRegex = new RegExp(`\\b${fieldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g')
      jsCondition = jsCondition.replace(fieldRegex, jsValue)
    })

    // Convert string comparisons to case-insensitive comparisons
    // Match patterns like: "value1" === "value2", "value1" === 'value2', 'value1' === "value2", or 'value1' === 'value2'
    // Replace with: "value1".toLowerCase() === "value2".toLowerCase()
    // Handles both double-quoted (JSON.stringify) and single-quoted (SOQL literal) strings
    jsCondition = jsCondition.replace(
      /(["'])((?:[^"\\]|\\.)*)\1\s*(===|!==)\s*(["'])((?:[^"\\]|\\.)*)\4/g,
      (_match, _leftQuote, leftValue, operator, _rightQuote, rightValue) => {
        return `"${leftValue}".toLowerCase() ${operator} "${rightValue}".toLowerCase()`
      },
    )

    return eval(jsCondition) === true
  } catch (error) {
    console.error('Error evaluating is_executed_condition:', error, { condition, event })
    return false // Default to not executed if evaluation fails
  }
}

/**
 * Evaluates is_executed_condition for PascalCase event objects.
 * This function handles events where field names have been converted to PascalCase
 * (e.g., Status_vod__c -> StatusVodC) but the condition still uses snake_case field names.
 *
 * @param condition - The condition string (e.g., "Status_vod__c = 'Submitted_vod'")
 * @param event - The event object with PascalCase properties (e.g., { StatusVodC: "Submitted_vod" })
 * @returns true if the event is executed/submitted, false otherwise
 */
export function evaluateIsExecutedForPascalCaseEvent(condition: string | undefined, event: any): boolean {
  if (!condition) return false

  try {
    let jsCondition = condition
      .replace(/<>/g, '!==')
      .replace(/!=/g, '!==')
      .replace(/([^!<>=])\s*=\s*([^=])/g, '$1 === $2')
      .replace(/\bNULL\b/gi, 'null')
      .replace(/\bTRUE\b/gi, 'true')
      .replace(/\bFALSE\b/gi, 'false')
      .replace(/\bAND\b/gi, '&&')
      .replace(/\bOR\b/gi, '||')
      .replace(/\bNOT\b/gi, '!')

    // Extract field names and replace them with PascalCase event property access
    const fieldPattern = /\b([A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)?(?:__[cr])?)\b/g
    const stringLiteralPattern = /(["'])(?:(?=(\\?))\2.)*?\1/g
    const conditionWithoutStrings = condition.replace(stringLiteralPattern, '')
    const fieldMatches = conditionWithoutStrings.match(fieldPattern) || []

    const soqlKeywords = new Set(['and', 'or', 'not', 'null', 'true', 'false', 'in', 'like'])

    const fieldNames = [...new Set(fieldMatches.filter((field) => !soqlKeywords.has(field.toLowerCase())))]

    fieldNames.forEach((fieldName) => {
      // Convert field name to PascalCase using the same logic as pascalCaseKeys
      // (e.g., Status_vod__c -> StatusVodC)
      const pascalCaseFieldName = titleCase(fieldName).replace(/ /g, '')

      // Try PascalCase version first
      let fieldValue = event[pascalCaseFieldName]
      // Fallback to original field name if PascalCase not found
      if (fieldValue === undefined) {
        fieldValue = event[fieldName]
      }
      // Fallback to lowercase original
      if (fieldValue === undefined) {
        fieldValue = event[fieldName.toLowerCase()]
      }

      const jsValue = fieldValue === null || fieldValue === undefined ? 'null' : JSON.stringify(fieldValue)
      const fieldRegex = new RegExp(`\\b${fieldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g')
      jsCondition = jsCondition.replace(fieldRegex, jsValue)
    })

    // Convert string comparisons to case-insensitive comparisons
    // Match patterns like: "value1" === "value2", "value1" === 'value2', 'value1' === "value2", or 'value1' === 'value2'
    // Replace with: "value1".toLowerCase() === "value2".toLowerCase()
    // Handles both double-quoted (JSON.stringify) and single-quoted (SOQL literal) strings
    jsCondition = jsCondition.replace(
      /(["'])((?:[^"\\]|\\.)*)\1\s*(===|!==)\s*(["'])((?:[^"\\]|\\.)*)\4/g,
      (_match, _leftQuote, leftValue, operator, _rightQuote, rightValue) => {
        return `"${leftValue}".toLowerCase() ${operator} "${rightValue}".toLowerCase()`
      },
    )

    return eval(jsCondition) === true
  } catch (error) {
    console.error('Error evaluating is_executed_condition:', error, {
      condition,
      event,
    })
    return false
  }
}

export function getEventColor(sfEvent: SfEvent): string {
  if (!sfEvent.WhatCount && !sfEvent.WhoCount) {
    return SfEventColors.TIME_OFF_TERRITORY
  }

  // Check if event is executed using the dynamic condition
  let isSubmitted = false

  if (sfEvent.is_executed_condition) {
    // Evaluate the condition - if true, event IS executed
    isSubmitted = evaluateIsExecutedCondition(sfEvent.is_executed_condition, sfEvent)
  } else {
    // Fallback: no condition means we treat event as not submitted
    isSubmitted = false
  }

  const now = new Date()

  if (isSubmitted) {
    return SfEventColors.SUBMITTED
  } else if (sfEvent.IsAllDayEvent) {
    const endDate = new Date(sfEvent.EndDateTime!)
    const todayStart = startOfDay(now)
    const endDayStart = startOfDay(endDate)

    return isAfter(todayStart, endDayStart) ? SfEventColors.PAST : SfEventColors.PLANNED
  } else if (isAfter(now, new Date(sfEvent.EndDateTime!))) {
    return SfEventColors.PAST
  } else {
    return SfEventColors.PLANNED
  }
}

export const minFutureDate = (date = new Date()): Date => add(date, { minutes: MIN_DIFFERENCE_IN_MINUTES })
export const minPastDate = (date = new Date()): Date => sub(date, { minutes: MIN_DIFFERENCE_IN_MINUTES })

export const setDateTime = (date: number | Date): number => getTime(set(date, { seconds: 0, milliseconds: 0 }))

export const isPastMaxDate = (date: number | Date, afterDate: number | Date): boolean =>
  isAfter(minFutureDate(new Date(date)), afterDate)
export const isBeforeMinDate = (date: number | Date, beforeDate: number | Date): boolean =>
  isBefore(minPastDate(new Date(date)), beforeDate)

export const DATE_TIME_FORMAT = 'yyyy / MM / dd - HH:mm'
export const MIN_DIFFERENCE_IN_MINUTES = 15

export interface BaseEventForm {
  title: string
  notes: string
  event: {
    start: number | null
    end: number | null
  }
  account: ComputedRef<SfAccountMini | null>
  contacts: ComputedRef<never[]>
  users: ComputedRef<SfUser[]>
  canvas: MiniCanvas
}

export interface PostCallForm extends BaseEventForm {
  account: ComputedRef<SfAccountMini | null>
  contacts: ComputedRef<never[]>
  users: ComputedRef<SfUser[]>
}
